import React, { useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { Copy, Check, Building2, User } from "lucide-react";

const RECIPIENT = "Ak Aydın Döviz ve Altın Ticareti Sınırlı Yetkili Müessese A.Ş.";

const BANKS = [
  {
    key: "vakif-katilim",
    name: "Vakıf Katılım",
    color: "#F7CE3E",
    textColor: "#0F172A",
    initials: "VK",
    domain: "vakifkatilim.com.tr",
    accounts: [
      { type: "TL", iban: "TR42 0021 0000 0001 4568 5000 01" },
      { type: "USD", iban: "TR31 0021 0000 0001 4568 5001 02" },
      { type: "EUR", iban: "TR58 0021 0000 0001 4568 5001 01" },
    ],
  },
  {
    key: "ziraat-mimar-sinan",
    name: "Ziraat Bankası",
    branch: "Mimar Sinan Şubesi",
    color: "#D8262C",
    textColor: "#FFFFFF",
    initials: "Z",
    domain: "ziraatbank.com.tr",
    accounts: [
      { type: "TL", iban: "TR12 0001 0015 4654 2987 7950 08" },
      { type: "USD", iban: "TR39 0001 0015 4654 2987 7950 07" },
      { type: "EUR", iban: "TR66 0001 0015 4654 2987 7950 06" },
    ],
  },
  {
    key: "ziraat-merkez",
    name: "Ziraat Bankası",
    branch: "Merkez Şubesi",
    featured: true,
    color: "#D8262C",
    textColor: "#FFFFFF",
    initials: "Z",
    domain: "ziraatbank.com.tr",
    accounts: [{ type: "TL", iban: "TR59 0001 0004 0454 2987 7950 02" }],
  },
  {
    key: "vakifbank",
    name: "VakıfBank",
    color: "#FBC72A",
    textColor: "#0F172A",
    initials: "VB",
    domain: "vakifbank.com.tr",
    accounts: [
      { type: "TL", iban: "TR41 0001 5001 5800 7320 1145 83" },
      { type: "USD", iban: "TR48 0001 5001 5804 8022 4983 42" },
      { type: "EUR", iban: "TR81 0001 5001 5804 8022 4983 30" },
    ],
  },
  {
    key: "isbankasi",
    name: "Türkiye İş Bankası",
    color: "#003C7E",
    textColor: "#FFFFFF",
    initials: "İŞ",
    domain: "isbank.com.tr",
    accounts: [
      { type: "TL", iban: "TR69 0006 4000 0012 5001 7502 71" },
      { type: "USD", iban: "TR42 0006 4000 0022 5000 7324 46" },
      { type: "EUR", iban: "TR04 0006 4000 0022 5000 7324 51" },
    ],
  },
  {
    key: "emlak-katilim",
    name: "Emlak Katılım",
    color: "#C8102E",
    textColor: "#FFFFFF",
    initials: "EK",
    domain: "emlakkatilim.com.tr",
    accounts: [
      { type: "TL", iban: "TR87 0021 1000 0005 7698 5000 01" },
      { type: "USD", iban: "TR06 0021 1000 0005 7698 5001 01" },
      { type: "EUR", iban: "TR76 0021 1000 0005 7698 5001 02" },
    ],
  },
  {
    key: "ziraat-katilim",
    name: "Ziraat Katılım",
    color: "#006A3A",
    textColor: "#FFFFFF",
    initials: "ZK",
    domain: "ziraatkatilim.com.tr",
    accounts: [
      { type: "TL", iban: "TR05 0020 9000 0163 0796 0000 01" },
      { type: "USD", iban: "TR75 0020 9000 0163 0796 0000 02" },
      { type: "EUR", iban: "TR48 0020 9000 0163 0796 0000 03" },
    ],
  },
  {
    key: "turkiye-finans",
    name: "Türkiye Finans",
    color: "#0058A3",
    textColor: "#FFFFFF",
    initials: "TF",
    domain: "turkiyefinans.com.tr",
    accounts: [
      { type: "TL", iban: "TR19 0020 6000 7700 7536 7700 01" },
      { type: "USD", iban: "TR08 0020 6000 7700 7536 7701 02" },
      { type: "EUR", iban: "TR35 0020 6000 7700 7536 7701 01" },
    ],
  },
];

function BankLogo({ bank }) {
  const [imgError, setImgError] = React.useState(false);
  if (imgError || !bank.domain) {
    return (
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center font-display font-bold text-base shrink-0 shadow-sm"
        style={{ background: bank.color, color: bank.textColor }}
      >
        {bank.initials}
      </div>
    );
  }
  return (
    <div className="w-14 h-14 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden p-2">
      <img
        src={`https://www.google.com/s2/favicons?domain=${bank.domain}&sz=128`}
        alt={bank.name}
        className="w-full h-full object-contain"
        loading="lazy"
        onError={() => setImgError(true)}
      />
    </div>
  );
}

const TYPE_STYLES = {
  TL: "bg-rose-50 text-rose-700 border-rose-200",
  USD: "bg-emerald-50 text-emerald-700 border-emerald-200",
  EUR: "bg-blue-50 text-blue-700 border-blue-200",
};

function CopyButton({ value, label = "Kopyala", testId }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
        copied
          ? "bg-emerald-50 text-emerald-700 border-emerald-300"
          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-950 hover:text-white hover:border-slate-950"
      }`}
      data-testid={testId}
      aria-label={`${label}: ${value}`}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Kopyalandı" : label}
    </button>
  );
}

export default function HesapNumaralari() {
  return (
    <>
      <PageHeader
        eyebrow="Kurumsal · Havale & EFT"
        title="Hesap Numaralarımız"
        description="Aşağıdaki banka hesaplarımıza havale veya EFT yapabilirsiniz. Lütfen alıcı adı olarak şirket ünvanımızı eksiksiz yazın."
      />

      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          {/* Alıcı Adı Kartı */}
          <div className="bg-slate-950 text-white rounded-2xl p-6 md:p-8 mb-10 flex flex-col md:flex-row md:items-center gap-5 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-brand-gold/10 blur-3xl" />
            <div className="relative w-12 h-12 rounded-full bg-brand-gold/15 flex items-center justify-center text-brand-gold shrink-0">
              <User className="w-5 h-5" />
            </div>
            <div className="relative flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.25em] text-brand-gold font-bold mb-1">
                Alıcı Adı
              </div>
              <div
                className="font-display text-lg md:text-xl font-semibold leading-snug break-words"
                data-testid="recipient-name"
              >
                {RECIPIENT}
              </div>
            </div>
            <div className="relative">
              <CopyButton value={RECIPIENT} testId="copy-recipient-button" />
            </div>
          </div>

          {/* Banka Kartları */}
          <div className="grid md:grid-cols-2 gap-5">
            {BANKS.map((bank) => (
              <div
                key={bank.key}
                className={`bg-white rounded-2xl border ${
                  bank.featured ? "border-brand-gold/60 shadow-lg shadow-brand-gold/10" : "border-slate-200"
                } overflow-hidden transition-all hover:shadow-xl hover:-translate-y-0.5`}
                data-testid={`bank-card-${bank.key}`}
              >
                {/* Header */}
                <div className="flex items-center gap-4 p-5 border-b border-slate-100">
                  <BankLogo bank={bank} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-slate-950 text-base md:text-lg leading-tight">
                      {bank.name}
                    </div>
                    {bank.branch && (
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {bank.branch}
                        {bank.featured && (
                          <span className="ml-1.5 px-1.5 py-0.5 rounded bg-brand-gold/15 text-brand-gold text-[9px] uppercase tracking-wider font-bold">
                            Öncelikli
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* IBAN Listesi */}
                <div className="p-4 space-y-2">
                  {bank.accounts.map((acc) => (
                    <div
                      key={acc.type}
                      className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100/80 transition-colors"
                    >
                      <span
                        className={`inline-flex items-center justify-center px-2.5 py-1 rounded-md border text-[11px] font-bold uppercase tracking-wider w-fit ${TYPE_STYLES[acc.type]}`}
                      >
                        {acc.type}
                      </span>
                      <span
                        className="font-mono-fin text-sm text-slate-950 flex-1 break-all select-all"
                        data-testid={`iban-${bank.key}-${acc.type.toLowerCase()}`}
                      >
                        {acc.iban}
                      </span>
                      <CopyButton
                        value={acc.iban.replace(/\s/g, "")}
                        label="Kopyala"
                        testId={`copy-${bank.key}-${acc.type.toLowerCase()}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Uyarı / Bilgi Notu */}
          <div className="mt-12 p-6 md:p-8 rounded-2xl bg-amber-50 border border-amber-200">
            <h3 className="font-display font-bold text-amber-900 text-base md:text-lg mb-3">
              Havale / EFT Yaparken Dikkat Edilmesi Gerekenler
            </h3>
            <ul className="space-y-2 text-sm text-amber-900 leading-relaxed">
              <li>
                <span className="font-semibold">•</span> Alıcı adı olarak mutlaka şirketimizin tam ünvanını yazınız.
              </li>
              <li>
                <span className="font-semibold">•</span> Açıklama bölümüne işlem tipinizi (USD alış, EUR satış, vb.) belirtmeniz işleminizi hızlandırır.
              </li>
              <li>
                <span className="font-semibold">•</span> Transfer sonrası dekontu{" "}
                <a href="tel:+905400804977" className="underline font-semibold">
                  0540 080 49 77
                </a>{" "}
                numaramıza WhatsApp ile iletmeniz, işlem onayını hızlandırır.
              </li>
              <li>
                <span className="font-semibold">•</span> Yatırım amaçlı işlemler hariç, döviz ve altın işlemlerinde TCMB ve BDDK mevzuatına uygun limit uygulanmaktadır.
              </li>
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
